// src/components/Recommendations/OptionWindow.js

import React, { useRef, useEffect, useState, useCallback } from 'react';
import TopRow from './TopRow';
import HookRow from './HookRow';
import DoctorsRow from './DoctorsRow';
import BannerHeader from './BannerHeader';
import AiCommentary from './AiCommentary';
import ReviewGrid from './ReviewGrid';
import GalleryRow from './GalleryRow';
import ClinicInfoText from './ClinicInfoText';
import FeaturedReviews from './FeaturedReviews';
import ContactForm from '../ContactForm';
import ClinicHighlights from './ClinicHighlights';
import Footer from '../Footer';
import recUtils from '../../utils/RecUtils';

/**
 * Progressive loading hook for clinic data
 * Loads data in stages: critical -> secondary -> heavy
 */
const useProgressiveDataLoading = (clinicName, isActive) => {
  const [loadingStage, setLoadingStage] = useState('initial');
  const [data, setData] = useState({
    basic: null,
    doctors: null,
    reviews: null
  });

  useEffect(() => {
    if (!isActive || !clinicName) return;

    const loadProgressively = async () => {
      try {
        // Stage 1: Critical data (immediate)
        setLoadingStage('basic');
        const basicData = {
          clinicName,
          isReady: true
        };
        setData(prev => ({ ...prev, basic: basicData }));

        // Stage 2: Secondary data (100ms delay for smooth UX)
        await new Promise(resolve => setTimeout(resolve, 100));
        setLoadingStage('secondary');
        
        // Doctors data is already loaded by parent component, just mark as ready
        setData(prev => ({ ...prev, doctors: { isReady: true } }));

        // Stage 3: Heavy data (300ms delay to prevent overwhelming)
        await new Promise(resolve => setTimeout(resolve, 300));
        setLoadingStage('complete');
        
        // Reviews and AI commentary are loaded on-demand by their components
        setData(prev => ({ ...prev, reviews: { isReady: true } }));

      } catch (error) {
        console.error('Progressive loading failed:', error);
        setLoadingStage('error');
      }
    };

    loadProgressively();
  }, [clinicName, isActive]);

  return { data, loadingStage };
};

/**
 * Hook for fetching clinic highlights data
 */
const useClinicHighlights = (clinicName, isActive) => {
  const [highlightsData, setHighlightsData] = useState({
    loading: true,
    reviewCount: 0,
    rating: 4.5,
    websiteReviewCounts: {},
    totalReviewCount: 0
  });

  useEffect(() => {
    if (!isActive || !clinicName) return;

    const fetchHighlightsData = async () => {
      setHighlightsData(prev => ({ ...prev, loading: true }));
      
      try {
        // Fetch review breakdown and satisfaction rating in parallel
        const [websiteBreakdown, averageSatisfaction] = await Promise.all([
          recUtils.getClinicWebsiteReviewBreakdown(clinicName),
          recUtils.getClinicAverageSatisfaction(clinicName)
        ]);

        setHighlightsData({
          loading: false,
          reviewCount: websiteBreakdown.totalReviewCount || 0,
          rating: parseFloat(averageSatisfaction.toFixed(2)),
          websiteReviewCounts: websiteBreakdown.websiteReviewCounts || {},
          totalReviewCount: websiteBreakdown.totalReviewCount || 0
        });
      } catch (error) {
        console.error('Error fetching clinic highlights data:', error);
        // Set fallback values on error
        setHighlightsData({
          loading: false,
          reviewCount: 0,
          rating: 4.5,
          websiteReviewCounts: {},
          totalReviewCount: 0
        });
      }
    };

    fetchHighlightsData();
  }, [clinicName, isActive]);

  return highlightsData;
};

/**
 * Lazy loading wrapper for heavy components
 * Only renders when the component becomes visible or after a delay
 */
const LazyComponentWrapper = ({ 
  children, 
  fallback = null, 
  delay = 0, 
  shouldRender = true,
  className = ""
}) => {
  const [isReady, setIsReady] = useState(delay === 0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (delay > 0 && shouldRender) {
      const timer = setTimeout(() => {
        setIsReady(true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [delay, shouldRender]);

  useEffect(() => {
    if (!ref.current || isVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px' // Start loading before visible
      }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  const shouldShowContent = shouldRender && isReady && (isVisible || delay === 0);

  return (
    <div ref={ref} className={className}>
      {shouldShowContent ? children : fallback}
    </div>
  );
};

/**
 * Collapsible Review Grid Toggle Component
 */
const ReviewGridToggle = ({ 
  isExpanded, 
  onToggle, 
  isFallbackMode = false,
  isMobile = false 
}) => {
  const buttonText = isExpanded 
    ? 'Hide reviews' 
    : isFallbackMode 
      ? 'See all clinic reviews' 
      : 'See all corresponding reviews';

  return (
    <div className={`flex justify-center rounded-lg border-2 border-gray-200 py-2 max-w-sm mx-auto ${isMobile ? 'mb-40' : ''}`}>
      <button
        onClick={onToggle}
        className="text-gray-400 underline font-bold hover:text-gray-600 transition-colors duration-200"
      >
        {buttonText}
      </button>
    </div>
  );
};

const OptionWindow = ({
  option,
  optionIndex = 0,
  isActive,
  contentRef,
  onScroll,
  isFallbackMode = false,
  isMobile = false,
  welcomePopupClosed = false,
}) => {
  /* Banner logic – only on mobile */
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const headerRef = useRef(null);
  const windowRef = useRef(null);
  const rightColumnRef = useRef(null);
  const hookRowRef = useRef(null);
  const contactFormContainerRef = useRef(null);

  // NEW: we'll capture the scrollable container here
  const scrollContainerRef = useRef(null);

  // Review grid visibility state
  const [isReviewGridExpanded, setIsReviewGridExpanded] = useState(false);

  // Prevent unneeded re-renders
  const lastScrollTop = useRef(0);
  const contactFormStyles = useRef({});

  // Progressive loading for clinic data
  const { data: progressiveData, loadingStage } = useProgressiveDataLoading(
    option?.title, 
    isActive
  );

  // Fetch clinic highlights data
  const clinicHighlights = useClinicHighlights(option?.title, isActive);

  // Show/hide banner when header scrolls out of view
  useEffect(() => {
    if (!isMobile || !isActive) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsBannerVisible(!entry.isIntersecting),
      { rootMargin: '-64px 0px 0px 0px' }
    );
    if (headerRef.current) observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [isActive, isMobile]);

  // Stick the contact form once you scroll past the HookRow
  const handleStickyContactForm = useCallback((e) => {
    if (isMobile || !isActive || !contactFormContainerRef.current || !hookRowRef.current) return;
    const scrollTop = e.target.scrollTop;
    const hookHeight = hookRowRef.current.offsetHeight;
    const hookOffset = hookRowRef.current.offsetTop;
    const containerWidth = rightColumnRef.current
      ? rightColumnRef.current.offsetWidth
      : 'auto';
    const stickyTopOffset = 20;
    const stickyStart = hookOffset + hookHeight - stickyTopOffset;

    if (scrollTop > stickyStart) {
      if (contactFormContainerRef.current.style.position !== 'sticky') {
        contactFormContainerRef.current.style.position = 'sticky';
        contactFormContainerRef.current.style.top = `${stickyTopOffset}px`;
        contactFormContainerRef.current.style.width = `${containerWidth}px`;
        contactFormContainerRef.current.offsetHeight = `${stickyTopOffset}px`;
      }
    } else {
      if (contactFormContainerRef.current.style.position !== 'static') {
        contactFormContainerRef.current.style.position = 'static';
        contactFormContainerRef.current.style.top = 'auto';
        contactFormContainerRef.current.style.width = 'auto';
      }
    }

    lastScrollTop.current = scrollTop;
  }, [isActive, isMobile]);

  // Combine parent scroll logic with our sticky logic
  const combinedScrollHandler = useCallback((e) => {
    if (onScroll) onScroll(e);
    handleStickyContactForm(e);
  }, [onScroll, handleStickyContactForm]);

  // Handle review grid toggle
  const handleReviewGridToggle = () => {
    setIsReviewGridExpanded(prev => !prev);
  };

  // Handle banner click to scroll to top
  const handleBannerScrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const recId = recUtils.getRecommendationId();
  const clinicName = option.title;
  
  // Get formData from recommendation document for FeaturedReviews
  const formData = recUtils.recommendationDoc?.user_input || {};

  // Destructure option data
  const {
    imageUrl = '',
    title = 'Option Title',
    headerInfo = 'Option description goes here',
    headerInfoLong = '',
    overallRating = 'Highly Rated',
    overallRatingColor = 'green',
    estimatedCost = '4000-7000',
    estimatedAvailability = 'Within 3 Months',
    rank = 1,
    doctors = null,
    hasMoreDoctors = false,
    galleryImages = []
  } = option || {};

  // Loading states for progressive rendering
  const isBasicReady = progressiveData.basic?.isReady || false;
  const isDoctorsReady = progressiveData.doctors?.isReady || false;
  const isReviewsReady = progressiveData.reviews?.isReady || false;

  return (
    <div
      ref={windowRef}
      className={`
        flex-none w-full flex flex-col
        h-full bg-transparent relative md:pt-8
        ${!isActive ? 'pointer-events-none' : ''}
      `}
    >
      {/* Banner only on mobile */}
      {isMobile && (
        <BannerHeader 
          option={option} 
          isVisible={isBannerVisible && isActive} 
          onClickScroll={handleBannerScrollToTop}
        />
      )}

      {/* Main scrollable content */}
      <div
        ref={el => {
          // capture for HookRow scroll and also forward to parent
          scrollContainerRef.current = el;
          contentRef(el);
        }}
        className="flex-1 min-h-0 overflow-y-auto"
        onScroll={combinedScrollHandler}
      >
        {/* MOBILE LAYOUT */}
        {isMobile && (
          <>
            <div className="md:mx-auto md:mb-4 md:overflow-hidden md:max-w-screen-md md:px-5 md:py-3">
              <div
                className="w-full bg-gray-50 md:bg-gray-100 flex-shrink-0"
              >
                <div className="md:flex md:flex-row md:py-5">
                  <div className="md:flex-1">
                    {/* TopRow - Always render immediately (critical) */}
                    <TopRow
                      ref={headerRef}
                      imageUrl={imageUrl}
                      optionTitle={title}
                      overallRating={overallRating}
                      overallRatingColor={overallRatingColor}
                      optionDescription={headerInfo}
                      optionDescriptionLong={headerInfoLong}
                      estimatedCost={estimatedCost}
                      estimatedAvailability={estimatedAvailability}
                      optionRank={rank}
                      galleryImages={galleryImages}
                      optionIndex={optionIndex}
                    />

                    {/* Clinic Highlights Section - Mobile Only */}
                    {clinicHighlights.loading ? (
                      <div className="px-5 mt-2">
                        <div className="bg-white rounded-lg shadow-md shadow-gray-400 p-4">
                          <div className="animate-pulse flex items-center justify-between">
                            <div className="flex-1 flex flex-col items-center">
                              <div className="bg-gray-200 rounded h-16 w-24 mb-2" />
                            </div>
                            <div className="w-px h-12 bg-gray-200 mx-3" />
                            <div className="flex-1 flex flex-col items-center">
                              <div className="bg-gray-200 rounded h-6 w-12 mb-2" />
                              <div className="bg-gray-200 rounded h-4 w-16" />
                            </div>
                            <div className="w-px h-12 bg-gray-200 mx-3" />
                            <div className="flex-1 flex flex-col items-center">
                              <div className="bg-gray-200 rounded h-6 w-8 mb-2" />
                              <div className="bg-gray-200 rounded h-4 w-20" />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <ClinicHighlights
                        rating={clinicHighlights.rating}
                        reviewCount={clinicHighlights.reviewCount}
                        websiteReviewCounts={clinicHighlights.websiteReviewCounts}
                        totalReviewCount={clinicHighlights.totalReviewCount}
                        isMobile={true}
                      />
                    )}
                  </div>
                  {/* HookRow - Progressive loading */}
                  {!isFallbackMode && (
                    <LazyComponentWrapper
                      shouldRender={isBasicReady}
                      delay={0}
                      fallback={
                        <div className="py-2 md:py-0 md:pr-4 md:w-auto md:flex-shrink-0">
                          <div className="animate-pulse bg-gray-200 rounded-lg h-32 w-full" />
                        </div>
                      }
                      className="py-2 md:py-0 md:pr-4 md:w-auto md:flex-shrink-0"
                    >
                      <HookRow
                        key={`hookrow-mobile-${option.id || optionIndex}`}
                        option={option}
                        isActive={isActive}
                        optionIndex={optionIndex}
                        scrollContainerRef={scrollContainerRef}
                        allowAnimations={welcomePopupClosed}
                      />
                    </LazyComponentWrapper>
                  )}
                </div>

                {/* FeaturedReviews - Progressive loading, above DoctorsRow */}
                {!isFallbackMode && (
                  <LazyComponentWrapper
                    shouldRender={isReviewsReady}
                    delay={150}
                    fallback={
                      <div className="px-5 py-4">
                        <div className="animate-pulse">
                          <div className="bg-gray-200 rounded h-6 w-48 mb-4" />
                          <div className="bg-gray-200 rounded-lg h-64 w-full" />
                        </div>
                      </div>
                    }
                    className="px-5 py-4"
                  >
                    <FeaturedReviews
                      clinicName={clinicName}
                      formData={formData}
                      isMobile={isMobile}
                    />
                  </LazyComponentWrapper>
                )}
                
                {/* DoctorsRow - Progressive loading */}
                <LazyComponentWrapper
                  shouldRender={isDoctorsReady}
                  delay={100}
                  fallback={
                    <div className="px-5 mt-2 pb-2">
                      <div className="animate-pulse">
                        <div className="bg-gray-200 rounded h-4 w-32 mb-2" />
                        <div className="grid grid-cols-2 gap-1">
                          <div className="bg-gray-200 rounded-lg h-16" />
                          <div className="bg-gray-200 rounded-lg h-16" />
                        </div>
                      </div>
                    </div>
                  }
                >
                  <DoctorsRow
                    doctors={doctors}
                    hasMoreDoctors={hasMoreDoctors}
                    isFallbackMode={isFallbackMode}
                  />
                </LazyComponentWrapper>
              </div>

              {/* AI Commentary - Heavy component, progressive loading */}
              {!isFallbackMode && (
                <LazyComponentWrapper
                  shouldRender={isReviewsReady}
                  delay={300}
                  fallback={
                    <div className="px-8 py-4">
                      <div className="my-1 border-t border-gray-500 border-opacity-100" />
                      <div className="animate-pulse">
                        <div className="bg-gray-200 rounded h-6 w-48 mb-4" />
                        <div className="space-y-2">
                          <div className="bg-gray-200 rounded h-4 w-full" />
                          <div className="bg-gray-200 rounded h-4 w-3/4" />
                          <div className="bg-gray-200 rounded h-4 w-5/6" />
                        </div>
                      </div>
                    </div>
                  }
                  className="px-8 py-4"
                >
                  <div className="my-1 border-t border-gray-500 border-opacity-100" />
                  <AiCommentary
                    recommendation_id={recId}
                    clinic_only_name={clinicName}
                  />
                </LazyComponentWrapper>
              )}
            </div>

            {/* Review Grid Toggle Button */}
            <div className="p-3 md:max-w-screen-5xl md:mx-auto">
              <div className="mt-1 mb-4 border-t-2 border-gray-200" />
              <ReviewGridToggle
                isExpanded={isReviewGridExpanded}
                onToggle={handleReviewGridToggle}
                isFallbackMode={isFallbackMode}
                isMobile={isMobile}
              />
            </div>

            {/* Collapsible Review Grid */}
            {isReviewGridExpanded && (
              <LazyComponentWrapper
                shouldRender={isReviewsReady}
                delay={0}
                fallback={
                  <div className="p-3 md:max-w-screen-5xl md:mx-auto">
                    <div className="animate-pulse">
                      <div className="bg-gray-200 rounded h-8 w-64 mx-auto mb-4" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="bg-gray-200 rounded-lg h-48" />
                        ))}
                      </div>
                    </div>
                  </div>
                }
                className="p-3 md:max-w-screen-5xl md:mx-auto"
              >
                <ReviewGrid
                  clinicOnlyName={clinicName}
                  isFallbackMode={isFallbackMode}
                />
              </LazyComponentWrapper>
            )}

            {/* Footer - Desktop only */}
            {!isMobile && <Footer />}
          </>
        )}

        {/* DESKTOP LAYOUT */}
        {!isMobile && (
          <>
            <div className="max-w-screen-xl mx-auto px-8 ">
              {/* Gallery Row - Critical, render immediately */}
              <GalleryRow
                imageUrl={imageUrl}
                galleryImages={galleryImages}
                optionTitle={title}
              />

              <div className="flex flex-row gap-8 mb-8">
                {/* Left side */}
                <div className="w-2/3">
                  {/* Clinic Info - Critical, render immediately */}
                  <ClinicInfoText
                    optionTitle={title}
                    optionDescription={headerInfo}
                    optionDescriptionLong={headerInfoLong}
                    estimatedCost={estimatedCost}
                    estimatedAvailability={estimatedAvailability}
                    clinicHighlights={clinicHighlights}
                  />
                  
                  {/* FeaturedReviews - Progressive loading, above DoctorsRow */}
                  {!isFallbackMode && (
                    <LazyComponentWrapper
                      shouldRender={isReviewsReady}
                      delay={150}
                      fallback={
                        <div className="mt-4 mb-8">
                          <div className="animate-pulse">
                            <div className="bg-gray-200 rounded h-6 w-48 mb-4" />
                            <div className="grid grid-cols-2 gap-6">
                              <div className="bg-gray-200 rounded-lg h-64" />
                              <div className="bg-gray-200 rounded-lg h-64" />
                            </div>
                          </div>
                        </div>
                      }
                      className="mt-4 mb-8"
                    >
                      <FeaturedReviews
                        clinicName={clinicName}
                        formData={formData}
                        isMobile={isMobile}
                      />
                    </LazyComponentWrapper>
                  )}
                  
                  {/* Doctors Row - Progressive loading */}
                  <LazyComponentWrapper
                    shouldRender={isDoctorsReady}
                    delay={100}
                    fallback={
                      <div className="mt-4 mb-8">
                        <div className="animate-pulse">
                          <div className="bg-gray-200 rounded h-6 w-48 mb-4" />
                          <div className="space-y-4">
                            {[...Array(2)].map((_, i) => (
                              <div key={i} className="bg-gray-200 rounded-lg h-32" />
                            ))}
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <DoctorsRow
                      doctors={doctors}
                      hasMoreDoctors={hasMoreDoctors}
                      isFallbackMode={isFallbackMode}
                      isMobile={isMobile}
                    />
                  </LazyComponentWrapper>

                  {/* AI Commentary - Heavy component, progressive loading */}
                  {!isFallbackMode && (
                    <LazyComponentWrapper
                      shouldRender={isReviewsReady}
                      delay={300}
                      fallback={
                        <div className="mt-4 mb-8">
                          <div className="my-1 border-t border-gray-500 border-opacity-100" />
                          <div className="animate-pulse">
                            <div className="bg-gray-200 rounded h-8 w-64 mb-4" />
                            <div className="space-y-2">
                              <div className="bg-gray-200 rounded h-4 w-full" />
                              <div className="bg-gray-200 rounded h-4 w-3/4" />
                              <div className="bg-gray-200 rounded h-4 w-5/6" />
                            </div>
                          </div>
                        </div>
                      }
                      className="mt-4"
                    >
                      <div className="my-1 border-t-2 border-gray-200 border-opacity-100" />
                      <AiCommentary
                        recommendation_id={recId}
                        clinic_only_name={clinicName}
                      />
                    </LazyComponentWrapper>
                  )}
                </div>

                {/* Right side */}
                <div className="w-1/3" ref={rightColumnRef}>
                  {/* HookRow - Progressive loading */}
                  {!isFallbackMode && (
                    <LazyComponentWrapper
                      shouldRender={isBasicReady}
                      delay={0}
                      fallback={
                        <div className="mb-6">
                          <div className="animate-pulse bg-gray-200 rounded-lg h-48" />
                        </div>
                      }
                      className="mb-6"
                    >
                      <div ref={hookRowRef}>
                        <HookRow
                          key={`hookrow-desktop-${option.id || optionIndex}`}
                          option={option}
                          isActive={isActive}
                          optionIndex={optionIndex}
                          scrollContainerRef={scrollContainerRef}
                          allowAnimations={welcomePopupClosed}
                        />
                      </div>
                    </LazyComponentWrapper>
                  )}
                  
                  {/* Contact Form - Always render (critical for conversions) */}
                  <div ref={contactFormContainerRef} className="form-container">
                    <ContactForm
                      selectedClinic={option}
                      key={`contact-form-${option.id}`}
                      hideAdditionalInfo={true}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Review Grid Section - Full width */}
            <div className="w-full bg-transparent pb-16">
              <div className="max-w-screen-3xl mx-auto px-8">
                <div className="mb-2" />
                
                {/* Review Grid Toggle Button */}
                <ReviewGridToggle
                  isExpanded={isReviewGridExpanded}
                  onToggle={handleReviewGridToggle}
                  isFallbackMode={isFallbackMode}
                  isMobile={isMobile}
                />

                {/* Collapsible Review Grid */}
                {isReviewGridExpanded && (
                  <LazyComponentWrapper
                    shouldRender={isReviewsReady}
                    delay={0}
                    fallback={
                      <div className="animate-pulse">
                        <div className="bg-gray-200 rounded h-8 w-64 mx-auto mb-6" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                          {[...Array(8)].map((_, i) => (
                            <div key={i} className="bg-gray-200 rounded-lg h-48" />
                          ))}
                        </div>
                      </div>
                    }
                  >
                    <ReviewGrid
                      clinicOnlyName={clinicName}
                      isFallbackMode={isFallbackMode}
                    />
                  </LazyComponentWrapper>
                )}
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
};

export default OptionWindow;